import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type KeyboardEvent
} from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageAssetType } from './carousel.types';

// Fraction of the visible viewport to scroll per nav-arrow click — slightly less than
// one page to keep at least one anchor thumbnail in view across a click.
const SCROLL_PAGE_FRACTION = 0.75;
// Pixel slack absorbed when checking scroll-edge state, accounting for sub-pixel rounding.
const SCROLL_EDGE_THRESHOLD_PX = 2;

interface ThumbnailSliderProps {
  images: ImageAssetType[];
  activeIndex: number;
  onSelect: (index: number) => void;
  cartAssetIds: Set<string>;
  focusOnMount?: boolean;
}

export default function ThumbnailSlider({
  images,
  activeIndex,
  onSelect,
  cartAssetIds,
  focusOnMount = false
}: ThumbnailSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [showControls, setShowControls] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (viewportRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = viewportRef.current;
      setCanScrollLeft(scrollLeft > SCROLL_EDGE_THRESHOLD_PX);
      setCanScrollRight(
        scrollLeft < scrollWidth - clientWidth - SCROLL_EDGE_THRESHOLD_PX
      );
    }
  }, []);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, images.length);
  }, [images]);

  useEffect(() => {
    if (focusOnMount && containerRef.current) {
      containerRef.current.focus();
    }
  }, [focusOnMount]);

  useEffect(() => {
    const checkScrollable = () => {
      if (viewportRef.current) {
        const { scrollWidth, clientWidth } = viewportRef.current;
        setShowControls(scrollWidth > clientWidth);
        updateScrollState();
      }
    };
    checkScrollable();
    const observer = new ResizeObserver(checkScrollable);
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [images, updateScrollState]);

  useEffect(() => {
    const activeItem = itemRefs.current[activeIndex];
    const viewport = viewportRef.current;
    if (activeItem && viewport) {
      const itemCenter = activeItem.offsetLeft + activeItem.offsetWidth / 2;
      const viewportCenter = viewport.clientWidth / 2;
      viewport.scrollTo({ left: itemCenter - viewportCenter, behavior: 'smooth' });
    }
  }, [activeIndex]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const handler = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        node.scrollLeft += e.deltaY;
      }
    };
    node.addEventListener('wheel', handler, { passive: false });
    return () => node.removeEventListener('wheel', handler);
  }, []);

  const handleViewportScroll = useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (viewportRef.current) {
      const scrollAmount =
        viewportRef.current.clientWidth * SCROLL_PAGE_FRACTION;
      viewportRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && activeIndex > 0) {
      e.preventDefault();
      onSelect(activeIndex - 1);
    } else if (e.key === 'ArrowRight' && activeIndex < images.length - 1) {
      e.preventDefault();
      onSelect(activeIndex + 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onSelect(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      onSelect(images.length - 1);
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex items-center w-full bg-neutral-900 p-3 gap-3',
        'relative overflow-hidden outline-none',
        '[contain:inline-size]'
      )}
      onKeyDown={handleKeyDown}
      role="listbox"
      aria-label="Image Thumbnails"
      tabIndex={0}
    >
      {showControls && (
        <NavButton
          direction="left"
          disabled={!canScrollLeft}
          onClick={() => handleScroll('left')}
        />
      )}

      {/* Fade gradient overlays */}
      <div
        className={cn(
          'absolute top-0 bottom-0 w-10 pointer-events-none z-[5]',
          'left-[44px] opacity-0 transition-opacity duration-300',
          'bg-gradient-to-r from-neutral-900/90 to-transparent',
          canScrollLeft && 'opacity-100'
        )}
        aria-hidden="true"
      />
      <div
        className={cn(
          'absolute top-0 bottom-0 w-10 pointer-events-none z-[5]',
          'right-[44px] opacity-0 transition-opacity duration-300',
          'bg-gradient-to-l from-neutral-900/90 to-transparent',
          canScrollRight && 'opacity-100'
        )}
        aria-hidden="true"
      />

      <div
        className="flex-1 min-w-0 overflow-x-auto flex gap-2 scroll-smooth p-1.5 relative scrollbar-none"
        ref={viewportRef}
        onScroll={handleViewportScroll}
      >
        {images.map((image, index) => {
          const isActive = index === activeIndex;
          const imageId = image?.assetUUID;
          const hasCartItem = imageId ? cartAssetIds.has(imageId) : false;
          const src = image.caches?.['300w'] || image.caches?.['830w'] || '';

          return (
            <button
              key={image.assetUUID || index}
              ref={(el) => { itemRefs.current[index] = el; }}
              className={cn(
                'shrink-0 w-[100px] h-[68px] cursor-pointer rounded',
                'bg-black/20 border-2 border-transparent outline-none',
                'transition-all duration-200 relative overflow-hidden p-0',
                isActive
                  ? 'border-white bg-white scale-[1.08] shadow-[0px_6px_12px_4px_rgba(0,0,0,0.2),0px_2px_4px_0px_rgba(0,0,0,0.3)] z-[2]'
                  : 'hover:opacity-85 hover:scale-[1.02]'
              )}
              onClick={() => onSelect(index)}
              role="option"
              aria-label={`View image ${index + 1}${hasCartItem ? ' (in cart)' : ''}`}
              aria-selected={isActive}
            >
              <img
                src={src}
                alt={image.caption || `Thumbnail ${index + 1}`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover block rounded-[3px]"
              />
              {hasCartItem && (
                <div
                  className={cn(
                    'absolute bottom-0 left-0 right-0 h-1/4',
                    'bg-success text-white flex items-center',
                    'justify-between px-1 z-10',
                    'rounded-b-[3px] animate-in fade-in slide-in-from-bottom',
                    isActive && 'bottom-0.5 left-0.5 right-0.5 rounded-b-[2px]'
                  )}
                >
                  <span className="text-[10px] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                    In Cart
                  </span>
                  <Check className="w-3 h-3 fill-white shrink-0" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {showControls && (
        <NavButton
          direction="right"
          disabled={!canScrollRight}
          onClick={() => handleScroll('right')}
        />
      )}
    </div>
  );
}

function NavButton({
  direction,
  disabled,
  onClick
}: {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      className={cn(
        'shrink-0 w-8 h-10 rounded-full border border-neutral-200',
        'bg-white flex items-center justify-center cursor-pointer',
        'transition-all duration-200 z-10',
        'disabled:cursor-default disabled:opacity-50',
        'hover:bg-neutral-100 hover:scale-105 active:scale-95',
        'disabled:hover:bg-white disabled:hover:scale-100'
      )}
      onClick={onClick}
      aria-label={`Scroll thumbnails ${direction}`}
      disabled={disabled}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}
